import type { Turn } from "../api/types";
import type { LogEntry } from "./types";

const CACHE_KEY = "wire_session_v1";

export interface CachedSession {
  sessionId: string;
  log: LogEntry[];
}

/**
 * Strip a turn to only what's needed to re-render on refresh. Crucially drops
 * `collected_data`/`debug` so the structured PII payload is never at rest in
 * the browser.
 */
function slimTurn(t: Turn): Turn {
  return {
    session_id: t.session_id,
    message: t.message,
    node_type: t.node_type,
    options: t.options ?? null,
    subtext: t.subtext ?? null,
    multi_select: t.multi_select,
    allow_free_text: t.allow_free_text,
    terminal: t.terminal,
    crisis: t.crisis,
    service_recommended: t.service_recommended ?? null,
    acknowledgment: t.acknowledgment ?? null,
    preamble: t.preamble ?? [],
    node_id: t.node_id ?? null,
    data_field: t.data_field ?? null,
    validation: t.validation ?? null,
    collected_data: null,
  };
}

export function saveSession(sessionId: string, log: LogEntry[]): void {
  try {
    const slim: LogEntry[] = log.map((e) =>
      e.role === "bot" ? { ...e, turn: slimTurn(e.turn) } : e,
    );
    localStorage.setItem(CACHE_KEY, JSON.stringify({ sessionId, log: slim }));
  } catch {
    /* storage disabled/full — caching is best-effort */
  }
}

export function loadSession(): CachedSession | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSession;
    if (!parsed?.sessionId || !Array.isArray(parsed.log) || parsed.log.length === 0) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}
